'use client';

import { type JSX, useState } from 'react';

import { Text } from '@videoblocks/design-system';

import { useChatSessionContext } from '../../_context/SessionContext';
import { useSearchResults } from '../../_hooks';
import { UsageLimitBanner } from '../browse-limit';
import { HelperCardCarousel } from '../helper-cards';
import { GoToKeywordSearchLink } from './GoToKeywordSearchLink';
import { ResultTypeToggle, type ResultContentType } from './ResultTypeToggle';
import { VideoResultsGridSkeleton } from './VideoResultsGridSkeleton';
import { AudioResultsGrid } from './audio/AudioResultsGrid';
import { ImageResultsGrid } from './image/ImageResultsGrid';
import { isImageBucketResult, isVideoBucketResult } from './resultBuckets';
import { VideoResultsGrid } from './video/VideoResultsGrid';

export function ResultsPane(): JSX.Element {
    const { results, totalCount, isSearching, hasSearched } = useSearchResults();
    const { hasReachedLimit, supportsAudio, supportsImages, currentTitle, showTopResults } = useChatSessionContext();
    const [selectedType, setSelectedType] = useState<ResultContentType>('video');

    const audioResults = results.filter((r) => r.contentClass === 'audio');
    const imageResults = results.filter((r) => isImageBucketResult(r, supportsImages));
    const videoResults = results.filter((r) => isVideoBucketResult(r, supportsImages));
    const typesWithResults: ResultContentType[] = [
        ...(videoResults.length > 0 ? ['video' as const] : []),
        ...(supportsAudio && audioResults.length > 0 ? ['audio' as const] : []),
        ...(imageResults.length > 0 ? ['image' as const] : []),
    ];
    const showToggle = supportsAudio || supportsImages;
    const activeType: ResultContentType = typesWithResults.includes(selectedType)
        ? selectedType
        : typesWithResults[0] ?? 'video';

    function renderContent(): JSX.Element {
        if (isSearching) return <VideoResultsGridSkeleton />;
        if (hasSearched && typesWithResults.length > 0) {
            if (activeType === 'audio') return <AudioResultsGrid audioItems={audioResults} />;
            if (activeType === 'image') return <ImageResultsGrid results={imageResults} />;
            return <VideoResultsGrid results={videoResults} />;
        }
        return <HelperCardCarousel />;
    }

    return (
        <div className="flex flex-col h-full @container">
            <div className="flex flex-col gap-2 bg-white border-b border-gray-200 px-6 py-3 @2xl:h-[--spacing(14.25)] @2xl:flex-row @2xl:items-center @2xl:justify-between @2xl:gap-4 @2xl:py-0">
                <div className="text-left">
                    <Text fontSize="base" fontWeight="semibold" className="leading-tight">
                        {currentTitle}
                    </Text>
                    <Text fontSize="sm" fontWeight="regular" className="text-gray-500">
                        Top {hasSearched ? totalCount : 0} Displayed
                    </Text>
                </div>
                {showToggle && (
                    <ResultTypeToggle
                        value={activeType}
                        onChange={setSelectedType}
                        videoCount={videoResults.length}
                        audioCount={supportsAudio ? audioResults.length : undefined}
                        imageCount={supportsImages ? imageResults.length : undefined}
                        className="w-full @2xl:w-auto"
                    />
                )}
                {/* DOM order (title → toggle → link) matches the wide layout for keyboard focus
                    order; the narrow stacked layout lifts the link to the top via `order-first`. */}
                <GoToKeywordSearchLink
                    contentClass={activeType}
                    className="order-first self-end @2xl:order-none @2xl:self-auto"
                />
            </div>
            {hasReachedLimit && <UsageLimitBanner />}
            <div className={showTopResults ? 'flex-1 overflow-y-auto bg-gray-100' : 'flex-1 overflow-y-auto'}>
                {renderContent()}
            </div>
        </div>
    );
}
